/**
 * AERO - Home page
 *
 * Pure selection logic plus the DOM layer for templates/index.liquid.
 * The logic is exported so it can be unit-tested under Node without a DOM;
 * this mirrors how assets/help.js and assets/cart-utils.js are structured.
 *
 * Owns four behaviours: scroll reveals, the product block (a gallery that both
 * rotates on a timer and answers the thumb rail), the specifications sticky
 * scroll, and the in-use video.
 *
 * The home page does not sell directly: size selection and add-to-cart live on
 * templates/product.liquid, and the buy panel links there.
 */
(function () {
  'use strict';

  /* ===== Pure logic (no DOM access below this line) ===== */

  /**
   * Resolve the variant matching a partial option selection.
   *
   * Retained as a tested export: the home page stopped selecting variants when
   * the buy panel became a link to the PDP, so nothing in the DOM layer below
   * calls this any more.
   *
   * The selection is partial by design: this product carries a single-value
   * Colour option that is hidden on the storefront, so only the positions the
   * reader actually chose are compared. A variant matches when every selected
   * position agrees; unselected positions are ignored.
   *
   * @param {Array<{id: number, options: string[]}>} variants
   * @param {Object<string|number, string>} selected - option position (1-based) to value
   * @returns {Object|null} the first matching variant, or null
   */
  function findVariant(variants, selected) {
    if (!Array.isArray(variants) || variants.length === 0) return null;

    const positions = Object.keys(selected || {});
    if (positions.length === 0) return variants[0];

    for (let i = 0; i < variants.length; i += 1) {
      const variant = variants[i];
      const options = (variant && variant.options) || [];
      let matches = true;

      for (let p = 0; p < positions.length; p += 1) {
        const position = parseInt(positions[p], 10);
        if (options[position - 1] !== selected[positions[p]]) {
          matches = false;
          break;
        }
      }

      if (matches) return variant;
    }

    return null;
  }

  /**
   * Step through a list that wraps at both ends.
   *
   * @param {number} current
   * @param {number} total
   * @param {number} step - may be negative
   * @returns {number} an index inside [0, total)
   */
  function nextIndex(current, total, step) {
    if (!total || total < 1) return 0;
    return (((current + step) % total) + total) % total;
  }

  /**
   * Fold a batch of IntersectionObserver updates into the panel the specifications
   * scroll should be naming.
   *
   * The observer watches a one-pixel line across the middle of the window (see
   * initFlow below), so at most one panel is intersecting at a time and a batch is
   * normally one panel leaving and one arriving. Anything not intersecting is
   * ignored rather than treated as a reason to change: at the two ends of the
   * section nothing crosses the line at all, and the title has to stay where it is
   * instead of falling back to the first panel.
   *
   * @param {number} current - the index on screen now
   * @param {Array<{index: number, isIntersecting: boolean}>} updates
   * @param {number} total - number of panels
   * @returns {number} the index to show
   */
  function resolveFlowIndex(current, updates, total) {
    if (!Array.isArray(updates) || !total || total < 1) return current;

    let next = current;

    for (let i = 0; i < updates.length; i += 1) {
      const update = updates[i];
      if (!update || !update.isIntersecting) continue;

      const index = update.index;
      /* indexOf returns -1 for a target that is not in the list, and a panel the
         markup never declared must not be able to move the title. */
      if (typeof index !== 'number' || index < 0 || index >= total) continue;
      if (Math.floor(index) !== index) continue;

      next = index;
    }

    return next;
  }

  /**
   * How much room the site header takes off the top of the window.
   *
   * sections/header.liquid pins .aero-navbar-section at top: 0 with z-index 2000
   * when its sticky setting is on, and that setting defaults to on — so anything
   * else pinned at top: 0 disappears underneath it. A header that is not pinned
   * scrolls away and reserves nothing, which is why the position matters as much
   * as the height.
   *
   * @param {string} position - the header's computed `position`
   * @param {number} height - its rendered height in px
   * @returns {number} px to clear, 0 when the header does not stay on screen
   */
  function headerOffset(position, height) {
    if (position !== 'sticky' && position !== 'fixed') return 0;
    if (typeof height !== 'number' || !isFinite(height) || height <= 0) return 0;
    return Math.round(height);
  }

  /**
   * Segundos → m:ss, que es como se lee un clip de 47 segundos.
   *
   * No recorta a 59 minutos a propósito: un vídeo de 61 minutos sale "61:05" en
   * vez de mentir con "1:05". Este reproductor no verá nunca esa cifra, pero una
   * función que devuelve el valor equivocado en su caso raro es una trampa
   * esperando a que alguien la reutilice.
   *
   * @param {number} seconds
   * @returns {string} m:ss, y '0:00' para NaN, negativos y cualquier no-número
   */
  function formatTime(seconds) {
    if (typeof seconds !== 'number' || !isFinite(seconds) || seconds <= 0) return '0:00';
    const total = Math.floor(seconds);
    const secs = total % 60;
    return Math.floor(total / 60) + ':' + (secs < 10 ? '0' : '') + secs;
  }

  /**
   * Dónde va el relleno de la barra, en porcentaje.
   *
   * `duration` es NaN hasta que llegan los metadatos, y con preload="none" eso
   * no pasa hasta que alguien pulsa play. Devolver 0 en ese hueco es lo que
   * mantiene la barra vacía en vez de llena o rota.
   *
   * @param {number} currentTime
   * @param {number} duration
   * @returns {number} 0..100
   */
  function seekPercent(currentTime, duration) {
    if (typeof duration !== 'number' || !isFinite(duration) || duration <= 0) return 0;
    if (typeof currentTime !== 'number' || !isFinite(currentTime) || currentTime <= 0) return 0;
    if (currentTime >= duration) return 100;
    return (currentTime / duration) * 100;
  }

  const api = { findVariant, nextIndex, resolveFlowIndex, headerOffset, formatTime, seekPercent };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.AeroHome = api;

  /* ===== DOM layer — everything below needs a browser ===== */

  if (typeof document === 'undefined') return;

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Scroll reveals ---------- */
  function initReveals() {
    const elements = document.querySelectorAll('.aero-reveal');
    if (elements.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
  }

  /* ---------- Product block ---------- */
  function initProduct() {
    const root = document.querySelector('[data-home-product]');
    if (!root) return;

    /* Image swap */
    const mainImage = root.querySelector('[data-home-product-image]');
    const media = root.querySelector('.aero-home__stage-media');
    const thumbs = Array.from(root.querySelectorAll('[data-home-thumb]'));

    /* Fewer than two images means nothing to swap between: Liquid does not even
       render the thumb rail in that case. */
    if (!mainImage || thumbs.length < 2) return;

    const FADE_MS = 260;
    /* A slow file must not strand the stage on the old photograph forever — past
       this the swap happens without the crossfade. */
    const LOAD_TIMEOUT_MS = 1500;
    const AUTOPLAY_DELAY = 5000;
    /* --home-ease, inlined: this is written to element.style, which cannot see a
       custom property declared on .aero-home. */
    const FADE_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

    let current = 0;
    /* Identifies the swap in flight. Anything older that resolves late sees a
       token that has moved on and stands down, so the last click always wins. */
    let swapToken = 0;
    let promoteTimer = null;
    let timer = null;
    let paused = false;
    let visible = !('IntersectionObserver' in window);

    /*
      A second, stacked copy of the stage image, built here rather than in Liquid so
      the markup degrades to a single photograph without JS.

      The swap used to fade the one <img> out, replace its src, and fade it back in,
      which left the stage empty for at least FADE_MS and up to LOAD_TIMEOUT_MS on an
      uncached file. Now the incoming photograph is decoded into this copy off screen,
      fades up over the outgoing one, and is only then promoted onto the real element.
      There is never a blank frame.

      It duplicates a photograph that is already described, so it is hidden from
      assistive technology rather than announced twice.
    */
    const ghost = mainImage.cloneNode(false);
    ghost.classList.add('aero-home__product-image--ghost');
    ghost.removeAttribute('data-home-product-image');
    ghost.setAttribute('alt', '');
    ghost.setAttribute('aria-hidden', 'true');
    ghost.setAttribute('loading', 'eager');
    mainImage.parentNode.appendChild(ghost);

    /* Back to invisible with no transition, so the next swap has a value to run from
       and a superseded fade never plays backwards. */
    function clearGhost() {
      ghost.style.transition = 'none';
      ghost.style.opacity = '0';
    }

    function showImage(index) {
      current = index;

      thumbs.forEach((thumb, i) => {
        const isActive = i === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      /* Claimed before the early return below, so going back to the image
         already on screen still cancels a swap that has not resolved yet. */
      swapToken += 1;
      const token = swapToken;

      if (promoteTimer) {
        window.clearTimeout(promoteTimer);
        promoteTimer = null;
      }

      const full = thumbs[index].dataset.full;
      if (!full || mainImage.getAttribute('src') === full) {
        /* Nothing to fetch — drop any fade the superseded swap left behind. */
        clearGhost();
        return;
      }

      if (prefersReducedMotion) {
        clearGhost();
        mainImage.src = full;
        return;
      }

      /* Snapping the ghost back to 0 here cannot flash: the outgoing photograph is
         still on mainImage underneath at full opacity. */
      clearGhost();

      let revealed = false;

      /* The crossfade has finished, so the photograph on top can become the real
         one. mainImage takes a URL the browser has just decoded, which paints
         immediately, so dropping the ghost a frame later exposes nothing. */
      function promote() {
        if (token !== swapToken) return;
        mainImage.src = full;
        window.requestAnimationFrame(() => {
          if (token !== swapToken) return;
          clearGhost();
        });
      }

      function reveal() {
        if (revealed || token !== swapToken) return;
        revealed = true;

        /* Flush the opacity: 0 written above, or the browser coalesces both values
           into one style recalculation and the transition never runs. */
        void ghost.offsetWidth;
        /* transform is restated because setting the shorthand drops the one the
           .aero-home__product-image rule declares, and the ghost has to keep easing
           into the hover scale alongside the image underneath it. */
        ghost.style.transition =
          'opacity ' + FADE_MS + 'ms ' + FADE_EASE + ', transform 0.4s ' + FADE_EASE;
        ghost.style.opacity = '1';
        promoteTimer = window.setTimeout(promote, FADE_MS);
      }

      /* onerror reveals on purpose: a broken image should still resolve the swap
         rather than leave the stage stuck on the previous photograph. */
      ghost.onload = reveal;
      ghost.onerror = reveal;
      ghost.src = full;
      if (ghost.complete) reveal();

      window.setTimeout(() => {
        if (revealed || token !== swapToken) return;
        revealed = true;
        promote();
      }, LOAD_TIMEOUT_MS);
    }

    function stopAutoplay() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function startAutoplay() {
      if (prefersReducedMotion || paused || !visible) return;
      stopAutoplay();
      timer = window.setInterval(() => {
        showImage(nextIndex(current, thumbs.length, 1));
      }, AUTOPLAY_DELAY);
    }

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        showImage(i);
        /* A click is a nudge, not a stop: restart the count so the chosen image
           gets its full five seconds before the rotation moves on. */
        startAutoplay();
      });
    });

    /* Hold the rotation while the reader is actually looking at or tabbing
       through the gallery, so it never pulls an image away mid-look. */
    if (media) {
      ['mouseenter', 'focusin'].forEach((type) => {
        media.addEventListener(type, () => {
          paused = true;
          stopAutoplay();
        });
      });

      ['mouseleave', 'focusout'].forEach((type) => {
        media.addEventListener(type, () => {
          paused = false;
          startAutoplay();
        });
      });
    }

    /* Only run the timer while the section is on screen, as the spec tabs do. */
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (visible) startAutoplay();
            else stopAutoplay();
          });
        },
        { threshold: 0.2 }
      );
      observer.observe(root);
    } else {
      startAutoplay();
    }
  }

  /* ---------- Specifications sticky scroll ---------- */
  function initFlow() {
    const root = document.querySelector('[data-flow]');
    if (!root) return;

    const items = Array.from(root.querySelectorAll('[data-flow-item]'));
    const ticks = Array.from(root.querySelectorAll('[data-flow-tick]'));
    const panels = Array.from(root.querySelectorAll('[data-flow-panel]'));
    if (items.length === 0 || panels.length === 0) return;

    /*
      Reserve the site header. home.css carries 67px and 79px as the two static
      values, which is what a reader with no JavaScript gets; measuring the real
      element is what keeps this right when the merchant switches the sticky
      header off in the theme editor, when the two heights change, and at the
      exact widths where header.css and home.css break at different pixels.
    */
    const header = document.querySelector('.aero-navbar-section');

    function syncHeader() {
      if (!header) return;
      const styles = window.getComputedStyle(header);
      const offset = headerOffset(styles.position, header.getBoundingClientRect().height);
      root.style.setProperty('--flow-header-h', offset + 'px');
    }

    syncHeader();

    /* ResizeObserver rather than a resize listener: the navbar changes height at a
       breakpoint, but it also changes when the logo setting does, and that fires no
       window event at all. */
    if ('ResizeObserver' in window && header) {
      new ResizeObserver(syncHeader).observe(header);
    } else {
      window.addEventListener('resize', syncHeader);
    }

    /* Liquid renders the first title active, so this starts in step with the DOM
       and a browser without IntersectionObserver still shows a titled section. */
    let current = 0;

    function show(index) {
      if (index === current) return;
      current = index;

      /*
        The change is a class, not a text rewrite: all four titles are in the
        markup, stacked in one cell, and home.css moves them.

        Both classes matter, and is-past is not decoration. It says a step has been
        read, which is how the CSS knows to park it above the frame rather than
        below it — so a title always leaves the way the page is moving without
        anything here having to track scroll direction. On the rail it is what
        makes the ticks read as distance covered instead of one lit segment.
      */
      items.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        item.classList.toggle('is-past', i < index);
      });

      ticks.forEach((tick, i) => {
        tick.classList.toggle('is-active', i === index);
        tick.classList.toggle('is-past', i < index);
      });
    }

    if (!('IntersectionObserver' in window)) return;

    /*
      The block does not appear until the reader arrives at it.

      Measured off vividmotion.co, the reference this section was modelled on: the
      two-column block is invisible until its own top crosses 40% of the window,
      then fades up over about a quarter of a second. Trimming 60% off the bottom
      of the observer root leaves a band from the top of the window down to that
      40% line, and this section is three screens tall — so it overlaps that band
      from the moment its top crosses the line until its bottom leaves the top of
      the window. `isIntersecting` therefore means exactly "the reader has reached
      this", on the way back up as well, where the reference puts it away again.

      Deliberately not .aero-reveal, which is how the rest of the page does this:
      that class translates as well as fades, and a transformed ancestor becomes
      the containing block for position: sticky — the pinned column would stop
      tracking the window. home.css carries the same note.
    */
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          root.classList.toggle('is-revealed', entry.isIntersecting);
        });
      },
      { rootMargin: '0px 0px -60% 0px', threshold: 0 }
    );

    revealObserver.observe(root);

    /*
      A threshold cannot express "the panel crossing the middle of the screen".
      These panels are a full viewport tall, so 0.5 is reached twice each — once
      arriving and once leaving — and two neighbours can hold it at the same time,
      which makes the title flicker between them.

      Shrinking the observer root to a thin band across the middle of the window
      asks the question that was actually meant: which panel is under the centre
      of the screen? With a band this narrow, one is, and only at a panel edge do
      two briefly qualify — which resolveFlowIndex settles.

      -49.5% each side and not -50%: the latter collapses the root to a rectangle
      of zero height, and a degenerate root is the one case engines have disagreed
      about. 1% of the window is ~9px, close enough to a line to read as one.
    */
    const observer = new IntersectionObserver(
      (entries) => {
        const updates = entries.map((entry) => ({
          index: panels.indexOf(entry.target),
          isIntersecting: entry.isIntersecting
        }));

        show(resolveFlowIndex(current, updates, panels.length));
      },
      { rootMargin: '-49.5% 0px -49.5% 0px', threshold: 0 }
    );

    panels.forEach((panel) => observer.observe(panel));
  }

  /* ---------- In-use video ---------- */
  /* The clip is 47 seconds of narration, not ambience, so this is a player and not
     a loop: nothing is fetched until someone presses the button (`preload="none"`
     in the markup), and nothing plays muted. There is deliberately no off-screen
     pause and no reduced-motion branch left here — both belonged to an autoplaying
     background loop. Someone who pressed play meant it, and scrolling past is not a
     reason to stop a video they are listening to.

     `controls` lives in the Liquid so the player still works with scripting off.
     This function's whole job is to put a poster-and-play-button state in front of
     those controls, which is why the buttons are inert in the markup and armed here.

     Los mandos son los mismos que los del riel de la ficha de producto: el botón
     grande cubre el marco entero y alterna play/pausa, se aparta mientras rueda y
     vuelve al pasar el ratón. La diferencia es el segundo botón, y la razón es que
     este vídeo va narrado — el sonido es el contenido, así que hay que poder
     quitarlo, y arranca puesto. */
  function initVideo() {
    const frame = document.querySelector('[data-home-video-frame]');
    if (!frame) return;

    const video = frame.querySelector('[data-home-video]');
    const button = frame.querySelector('[data-home-video-play]');
    const sound = frame.querySelector('[data-home-video-sound]');
    const bar = frame.querySelector('[data-home-video-bar]');
    const seek = frame.querySelector('[data-home-video-seek]');
    const currentOut = frame.querySelector('[data-home-video-current]');
    const durationOut = frame.querySelector('[data-home-video-duration]');
    if (!video || !button) return;

    if (bar) bar.hidden = false;

    [button, sound].forEach((el) => {
      if (!el) return;
      el.removeAttribute('tabindex');
      el.removeAttribute('aria-hidden');
    });
    frame.classList.add('is-armed');

    /* The overlay's scrim is translucent, so leaving `controls` on would show the
       native bar through it — dimmed, unreachable, and sitting under a button that
       says the video has not started. Taken off here and never handed back: desde
       que hay JS, los mandos son estos dos. Esta línea no se ejecuta nunca sin JS,
       así que quien tenga el scripting apagado conserva el reproductor nativo
       entero, barra de progreso incluida. */
    video.removeAttribute('controls');

    function label(el, name) {
      const text = el.getAttribute(name);
      if (text) el.setAttribute('aria-label', text);
    }

    /* El estado lo pintan los eventos del propio <video>, no el manejador del
       clic: así los iconos y las etiquetas siguen siendo ciertos cuando la pausa
       no la ha pedido el botón — un play() rechazado, el usuario saliendo de la
       pestaña, o el vídeo llegando al final. */
    video.addEventListener('play', () => {
      frame.classList.add('is-started', 'is-playing');
      label(button, 'data-label-pause');
    });

    video.addEventListener('pause', () => {
      frame.classList.remove('is-playing');
      label(button, 'data-label-play');
    });

    button.addEventListener('click', () => {
      if (video.paused) {
        const played = video.play();
        if (played && typeof played.catch === 'function') {
          /* A refused play leaves the poster up, so the button has to come back with
             it — otherwise the frame is a dead still with no way to start it. */
          played.catch(() => frame.classList.remove('is-playing'));
        }
      } else {
        video.pause();
      }
    });

    if (sound) {
      /* volumechange es quien manda, y no el clic, porque `muted` lo puede mover
         algo que no sea este botón. */
      video.addEventListener('volumechange', () => {
        sound.classList.toggle('is-muted', video.muted);
        label(sound, video.muted ? 'data-label-unmute' : 'data-label-mute');
      });

      label(sound, 'data-label-mute');
      sound.addEventListener('click', () => {
        video.muted = !video.muted;
      });
    }

    if (seek) {
      /* `scrubbing` es la única pieza de estado que hace falta: mientras se
         arrastra, timeupdate sigue llegando con el tiempo VIEJO del vídeo y
         pisaría la posición que el dedo está eligiendo. La barra manda mientras
         se toca, y el vídeo vuelve a mandar al soltar. */
      let scrubbing = false;

      function paint() {
        const percent = api.seekPercent(video.currentTime, video.duration);
        if (!scrubbing) seek.value = String(percent);
        seek.style.setProperty('--seek-progress', percent + '%');

        /* aria-valuetext, no el número: el range va de 0 a 100 y "26" no le dice
           nada a nadie. El orden de la frase lo pone la traducción. */
        const template = seek.getAttribute('data-position-template');
        if (template) {
          seek.setAttribute(
            'aria-valuetext',
            template
              .replace('%CURRENT%', api.formatTime(video.currentTime))
              .replace('%TOTAL%', api.formatTime(Math.round(video.duration)))
          );
        }
        if (currentOut) currentOut.textContent = api.formatTime(video.currentTime);
      }

      /* Hasta que no llegan los metadatos no hay duración, y sin duración no se
         puede convertir un porcentaje en segundos. Con preload="none" eso no
         pasa hasta el primer play, así que la barra sale desactivada y se abre
         aquí — un control que acepta arrastres que no van a ningún sitio es
         peor que uno que dice que todavía no. */
      video.addEventListener('loadedmetadata', () => {
        if (!isFinite(video.duration) || video.duration <= 0) return;
        seek.disabled = false;
        /* El total se REDONDEA y el transcurrido se trunca, y no es un descuido
           que difieran: este clip dura 46,98 s, así que truncándolo anunciaba
           "0:46" — un segundo menos de lo que dura y de lo que dice la línea de
           arriba ("47 seconds"). El transcurrido sí se trunca, o marcaría el
           final antes de llegar. */
        if (durationOut) durationOut.textContent = api.formatTime(Math.round(video.duration));
        paint();
      });

      video.addEventListener('timeupdate', paint);

      /* pointerdown y no 'input' para abrir el arrastre: el teclado también
         dispara 'input' y ahí no hay nada que arrastrar. */
      seek.addEventListener('pointerdown', () => {
        scrubbing = true;
      });

      /* En window y no en el propio control: el dedo se suelta a menudo fuera de
         la barra, y ahí el evento no llegaría nunca — la barra se quedaría
         congelada creyendo que aún la están arrastrando. */
      window.addEventListener('pointerup', () => {
        scrubbing = false;
      });

      seek.addEventListener('input', () => {
        if (!isFinite(video.duration) || video.duration <= 0) return;
        video.currentTime = (Number(seek.value) / 100) * video.duration;
        seek.style.setProperty('--seek-progress', seek.value + '%');
        if (currentOut) currentOut.textContent = api.formatTime(video.currentTime);
      });

      paint();
    }

    /* Back to the poster at the end rather than holding on the last frame. load()
       restores it without refetching — preload is "none", so nothing is pulled
       again until the button is pressed a second time. */
    video.addEventListener('ended', () => {
      frame.classList.remove('is-started', 'is-playing');
      video.load();
      if (seek) {
        seek.value = '0';
        seek.style.setProperty('--seek-progress', '0%');
      }
      if (currentOut) currentOut.textContent = api.formatTime(0);
    });
  }

  function init() {
    initReveals();
    initProduct();
    initFlow();
    initVideo();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
